import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { elUpperCase } from '../utils/text';
import {
  Box, Typography, Paper, TextField, Button, Alert,
  CircularProgress, Avatar, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import IosShareIcon from '@mui/icons-material/IosShare';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
import packageJson from '../../package.json';

const APP_VERSION = packageJson.version;

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const RED = '#ef4444';

const inputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: '"Outfit", sans-serif',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 2 },
  },
  '& .MuiInputBase-input': { fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', color: NAV_BG },
};

export default function ParentSettingsPage() {
  const { authTokens, backendUrl, logoutUser, subscribeToPush, unsubscribeFromPush, getPushStatus } = useContext(AuthContext);
  const { t } = useTranslation();

  const [profile, setProfile] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [allowMessages, setAllowMessages] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState('');

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [snack, setSnack] = useState(null);

  // Web push state: loading | unsupported | denied | not_asked | not_subscribed | subscribed
  const [webPushStatus, setWebPushStatus] = useState('loading');
  const [webPushWorking, setWebPushWorking] = useState(false);
  // Mobile notifications (backend flag)
  const [mobileNotif, setMobileNotif] = useState(true);
  const [mobileNotifSaving, setMobileNotifSaving] = useState(false);
  // Per-event notification preferences
  const [notifyRouteStart, setNotifyRouteStart] = useState(true);
  const [notifyBusNear, setNotifyBusNear] = useState(true);
  const [notifyBusArriving, setNotifyBusArriving] = useState(true);
  const [notifyPickDrop, setNotifyPickDrop] = useState(true);
  const [notifyMessages, setNotifyMessages] = useState(true);
  const [notifyDelays, setNotifyDelays] = useState(true);
  const [notifPrefsSaving, setNotifPrefsSaving] = useState(false);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  useEffect(() => {
    getPushStatus().then(setWebPushStatus);
  }, []);

  const handleWebPushToggle = async () => {
    setWebPushWorking(true);
    if (webPushStatus === 'subscribed') {
      const result = await unsubscribeFromPush();
      if (result === 'unsubscribed') setWebPushStatus('not_subscribed');
      else setSnack({ severity: 'error', msg: t('common.network_error') });
    } else {
      const result = await subscribeToPush();
      if (result === 'subscribed' || result === 'already_subscribed') {
        setWebPushStatus('subscribed');
        setSnack({ severity: 'success', msg: t('parent_settings_page.notifications_enabled') });
        localStorage.removeItem('sb_push_prompt_dismissed');
      } else if (result === 'denied') {
        setWebPushStatus('denied');
      } else if (result === 'unsupported') {
        setWebPushStatus('unsupported');
      } else {
        setSnack({ severity: 'error', msg: t('common.network_error') });
      }
    }
    setWebPushWorking(false);
  };

  const handleNotifPrefChange = async (field, value, setter) => {
    setter(value);
    setNotifPrefsSaving(true);
    try {
      await fetch(backendUrl + 'api/parent-profile/', {
        method: 'PATCH', headers: authHeader,
        body: JSON.stringify({ [field]: value }),
      });
    } catch {
      setter(!value); // revert
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setNotifPrefsSaving(false);
  };

  const handleMobileNotifToggle = async (enabled) => {
    setMobileNotifSaving(true);
    setMobileNotif(enabled);
    try {
      await fetch(backendUrl + 'api/parent-profile/', {
        method: 'PATCH', headers: authHeader,
        body: JSON.stringify({ push_notifications_enabled: enabled }),
      });
    } catch {
      setMobileNotif(!enabled); // revert
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setMobileNotifSaving(false);
  };

  useEffect(() => {
    fetch(backendUrl + 'api/parent-profile/', { headers: authHeader })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setProfile(data);
          setFirstName(data.firstname || data.first_name || '');
          setLastName(data.lastname || data.last_name || '');
          setPhone(data.phone_number || '');
          setAllowMessages(data.allow_messages !== false);
          setMobileNotif(data.push_notifications_enabled !== false);
          setNotifyRouteStart(data.routeStart_notify !== false);
          setNotifyBusNear(data.busNear_notify !== false);
          setNotifyBusArriving(data.busArriving_notify !== false);
          setNotifyPickDrop(data.pickDrop_notify !== false);
          setNotifyMessages(data.messages_notify !== false);
          setNotifyDelays(data.delays_notify !== false);
        } else {
          setSnack({ severity: 'error', msg: t('common.network_error') });
        }
      })
      .catch(() => setSnack({ severity: 'error', msg: t('common.network_error') }));
  }, []);

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileError('');
    setProfileSaved(false);
    try {
      const res = await fetch(backendUrl + 'api/parent-profile/', {
        method: 'PATCH', headers: authHeader,
        body: JSON.stringify({ first_name: firstName, last_name: lastName, phone_number: phone, allow_messages: allowMessages }),
      });
      if (res.ok) {
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
      } else {
        setProfileError(t('parent_settings_page.save_failed'));
      }
    } catch {
      setProfileError(t('common.network_error'));
    }
    setProfileSaving(false);
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (newPw !== confirmPw) { setPwError(t('preferences.password_mismatch')); return; }
    if (newPw.length < 6) { setPwError(t('parent_settings_page.password_min_length')); return; }
    setPwLoading(true);
    try {
      const res = await fetch(backendUrl + 'api/parent/change-password/', {
        method: 'POST', headers: authHeader,
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwSuccess(t('preferences.password_saved'));
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
      } else {
        setPwError(data.error || t('parent_settings_page.password_change_failed'));
      }
    } catch {
      setPwError(t('common.network_error'));
    }
    setPwLoading(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const res = await fetch(backendUrl + 'api/parent/delete-account/', {
        method: 'DELETE', headers: authHeader,
        body: JSON.stringify({ password: deletePassword }),
      });
      if (res.ok) {
        setDeleteDialog(false);
        setSnack({ severity: 'success', msg: t('parent_settings_page.account_deleted') });
        setTimeout(() => logoutUser(), 2000);
      } else {
        const data = await res.json().catch(() => ({}));
        setSnack({ severity: 'error', msg: data.error || t('parent_settings_page.delete_failed') });
      }
    } catch {
      setSnack({ severity: 'error', msg: t('common.network_error') });
    }
    setDeleting(false);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'el' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('sb_language', newLang);
    fetch(backendUrl + 'api/parent-profile/', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access },
      body: JSON.stringify({ lang_pref: newLang }),
    }).catch(() => {});
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', p: 3 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.4rem', color: NAV_BG, mb: 0.5 }}>
        {t('nav.parent_settings')}
      </Typography>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', mb: 3 }}>
        {t('parent_settings_page.subtitle')}
      </Typography>

      {/* Profile card */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ width: 52, height: 52, background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.1rem' }}>
          {profile ? `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}` : <PersonIcon />}
        </Avatar>
        <Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
            {firstName || lastName ? `${firstName} ${lastName}`.trim() : (profile?.username || '...')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#64748b' }}>{t('parent_settings_page.role_parent')}</Typography>
        </Box>
      </Paper>

      {/* Personal info */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 2 }}>
          {t('preferences.personal_info')}
        </Typography>
        {profileSaved && <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>{t('preferences.saved')}</Alert>}
        {profileError && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{profileError}</Alert>}

        <TextField fullWidth size="small" label={t('preferences.first_name')} value={firstName}
          onChange={e => setFirstName(e.target.value)} sx={{ ...inputSx, '& label': { fontFamily: '"Outfit", sans-serif' } }} InputLabelProps={{ sx: { fontFamily: '"Outfit", sans-serif' } }} />
        <TextField fullWidth size="small" label={t('preferences.last_name')} value={lastName}
          onChange={e => setLastName(e.target.value)} sx={{ ...inputSx, '& label': { fontFamily: '"Outfit", sans-serif' } }} InputLabelProps={{ sx: { fontFamily: '"Outfit", sans-serif' } }} />
        <TextField fullWidth size="small" label={t('preferences.phone_number')} value={phone}
          onChange={e => setPhone(e.target.value.replace(/[^0-9+\- ]/g, ''))} sx={{ ...inputSx, '& label': { fontFamily: '"Outfit", sans-serif' } }} InputLabelProps={{ sx: { fontFamily: '"Outfit", sans-serif' } }} />

        <FormControlLabel
          control={<Switch checked={allowMessages} onChange={e => setAllowMessages(e.target.checked)} sx={{ '& .MuiSwitch-thumb': { background: AMBER }, '& .Mui-checked + .MuiSwitch-track': { background: AMBER + '80' } }} />}
          label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: NAV_BG }}>{t('parent_settings_page.allow_messages')}</Typography>}
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained" disableElevation onClick={handleSaveProfile}
          disabled={profileSaving}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
            textTransform: 'none', borderRadius: 1.5, px: 3,
          }}
        >
          {profileSaving ? <CircularProgress size={16} sx={{ color: NAV_BG }} /> : t('preferences.save')}
        </Button>
      </Paper>

      {/* Change password */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 2 }}>
          {t('preferences.change_password')}
        </Typography>
        {pwSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>{pwSuccess}</Alert>}
        {pwError && <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>{pwError}</Alert>}

        <TextField
          fullWidth size="small" type={showCurrent ? 'text' : 'password'}
          placeholder={t('parent_settings_page.current_password')} value={currentPw} onChange={e => setCurrentPw(e.target.value)}
          sx={inputSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowCurrent(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8' }}>
                  {showCurrent ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth size="small" type={showNew ? 'text' : 'password'}
          placeholder={t('preferences.new_password')} value={newPw} onChange={e => setNewPw(e.target.value)}
          sx={inputSx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew(p => !p)} edge="end" size="small" tabIndex={-1} sx={{ color: '#94a3b8' }}>
                  {showNew ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth size="small" type="password"
          placeholder={t('preferences.confirm_password')} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
          sx={{ ...inputSx, mb: 2 }}
        />
        <Button
          variant="contained" disableElevation onClick={handleChangePassword}
          disabled={pwLoading || !currentPw || !newPw || !confirmPw}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
            textTransform: 'none', borderRadius: 1.5, px: 3,
          }}
        >
          {pwLoading ? <CircularProgress size={16} sx={{ color: NAV_BG }} /> : t('preferences.update_password')}
        </Button>
      </Paper>

      {/* Language */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 1.5 }}>
          Language / Γλώσσα
        </Typography>
        <Button
          variant="outlined" onClick={toggleLanguage}
          sx={{
            fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none',
            borderRadius: 1.5, borderColor: AMBER, color: AMBER_DARK,
            '&:hover': { background: 'rgba(245,158,11,0.06)', borderColor: AMBER },
          }}
        >
          {i18n.language === 'en' ? 'Switch to Greek (ΕΛ)' : 'Switch to English (EN)'}
        </Button>
      </Paper>

      {/* Notifications */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 2 }}>
          {t('parent_settings_page.notifications_title')}
        </Typography>

        {/* Web push row */}
        {webPushStatus === 'unsupported' && isIOS() && !isStandalone() ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, p: 1.5, background: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <IosShareIcon sx={{ color: AMBER, fontSize: 20, mt: 0.2, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#475569' }}>
              {t('parent_settings_page.ios_push_hint')}
            </Typography>
          </Box>
        ) : webPushStatus !== 'unsupported' && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {webPushStatus === 'subscribed'
                  ? <NotificationsActiveIcon sx={{ color: '#10b981', fontSize: 18 }} />
                  : <NotificationsOffIcon sx={{ color: '#94a3b8', fontSize: 18 }} />}
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', fontWeight: 600, color: NAV_BG }}>
                  {t('parent_settings_page.web_notifications')}
                </Typography>
              </Box>
              {webPushStatus === 'denied' ? (
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8' }}>
                  {t('parent_settings_page.blocked')}
                </Typography>
              ) : (
                <Switch
                  checked={webPushStatus === 'subscribed'}
                  disabled={webPushWorking || webPushStatus === 'loading'}
                  onChange={handleWebPushToggle}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER_DARK }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }}
                />
              )}
            </Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b', pl: '26px' }}>
              {webPushStatus === 'denied'
                ? t('parent_settings_page.notifications_unblock_hint')
                : webPushStatus === 'subscribed'
                ? t('parent_settings_page.notifications_active')
                : t('parent_settings_page.notifications_desc')}
            </Typography>
          </Box>
        )}

        {/* Mobile push row */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {mobileNotif
                ? <NotificationsActiveIcon sx={{ color: '#10b981', fontSize: 18 }} />
                : <NotificationsOffIcon sx={{ color: '#94a3b8', fontSize: 18 }} />}
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', fontWeight: 600, color: NAV_BG }}>
                {t('parent_settings_page.mobile_notifications')}
              </Typography>
            </Box>
            <Switch
              checked={mobileNotif}
              disabled={mobileNotifSaving}
              onChange={e => handleMobileNotifToggle(e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER_DARK }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }}
            />
          </Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b', pl: '26px' }}>
            {t('parent_settings_page.mobile_notifications_desc')}
          </Typography>
        </Box>

        {/* Per-event notification preferences */}
        <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 2 }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', fontWeight: 600, color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {elUpperCase(t('parent_settings_page.notify_types_title'))}
          </Typography>
          {[
            { field: 'routeStart_notify',  label: t('parent_settings_page.notify_route_start'),    desc: t('parent_settings_page.notify_route_start_desc'),    value: notifyRouteStart,   setter: setNotifyRouteStart   },
            { field: 'busNear_notify',     label: t('parent_settings_page.notify_bus_near'),       desc: t('parent_settings_page.notify_bus_near_desc'),       value: notifyBusNear,      setter: setNotifyBusNear      },
            { field: 'busArriving_notify', label: t('parent_settings_page.notify_bus_arriving'),   desc: t('parent_settings_page.notify_bus_arriving_desc'),   value: notifyBusArriving,  setter: setNotifyBusArriving  },
            { field: 'pickDrop_notify',    label: t('parent_settings_page.notify_pick_drop'),      desc: t('parent_settings_page.notify_pick_drop_desc'),      value: notifyPickDrop,     setter: setNotifyPickDrop     },
            { field: 'messages_notify',    label: t('parent_settings_page.notify_messages'),       desc: t('parent_settings_page.notify_messages_desc'),       value: notifyMessages,     setter: setNotifyMessages     },
            { field: 'delays_notify',      label: t('parent_settings_page.notify_delays'),         desc: t('parent_settings_page.notify_delays_desc'),         value: notifyDelays,       setter: setNotifyDelays       },
          ].map(({ field, label, desc, value, setter }) => (
            <Box key={field} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500 }}>{label}</Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.77rem', color: '#94a3b8' }}>{desc}</Typography>
              </Box>
              <Switch
                checked={value}
                disabled={notifPrefsSaving}
                onChange={e => handleNotifPrefChange(field, e.target.checked, setter)}
                size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER_DARK }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }}
              />
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Danger zone */}
      <Paper sx={{ p: 2.5, borderRadius: 2, border: `1px solid #fee2e2`, background: '#fff5f5' }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: RED, mb: 1 }}>
          {t('parent_settings_page.danger_zone')}
        </Typography>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
          {t('parent_settings_page.danger_desc')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DeleteForeverIcon />}
          onClick={() => { setDeletePassword(''); setDeleteDialog(true); }}
          sx={{
            fontFamily: '"Outfit", sans-serif', fontWeight: 600, textTransform: 'none',
            borderRadius: 1.5, borderColor: RED, color: RED,
            '&:hover': { background: '#fff5f5', borderColor: '#dc2626', color: '#dc2626' },
          }}
        >
          {t('parent_settings_page.delete_account')}
        </Button>
      </Paper>

      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', mt: 3 }}>
        {t('common.app_version', { version: APP_VERSION })}
      </Typography>

      {/* Delete account dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: RED }}>
          {t('parent_settings_page.delete_account')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: '#64748b', mb: 2 }}>
            {t('parent_settings_page.delete_confirm')}
          </Typography>
          <TextField
            fullWidth size="small" type="password" placeholder={t('parent_settings_page.password_placeholder')}
            value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            variant="contained" disableElevation onClick={handleDeleteAccount}
            disabled={deleting || !deletePassword}
            sx={{ background: RED, color: 'white', fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', borderRadius: 1.5, '&:hover': { background: '#dc2626' } }}
          >
            {deleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : t('parent_settings_page.delete_btn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={20000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ top: { xs: 'calc(8px + var(--sat))', sm: 'calc(24px + var(--sat))' } }}>
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
